import React, {useEffect, useState, useCallback} from 'react';
import _ from 'lodash';

import {useAutocomplete, performAutocomplete} from '../../ducks/autocomplete.js';

const AutocompleteContext = React.createContext({});

function Autocomplete({entityType, values, setFieldError, setFieldValue, children}) {
    let {autocomplete} = useAutocomplete(entityType.dataTypeId);

    const [autocompleteState, setAutocompleteState] = useState({
        // the values on which we last performed the autocomplete
        lastRanOn: values,

        // loading is a map of a field name, to the number of autocomplete operations currently running on that field
        loading: {},

        // map of field name to the status of the most recent autocomplete that finished
        statuses: {},

        messages: {},
    });

    const checkForAutocompletes = useCallback(
        _.debounce((values) => {
            const prevValues = autocompleteState.lastRanOn;
            if (!prevValues) return;

            _.map(values, (value, key) => {
                let prevValue = _.get(prevValues, key);
                if (!_.isEqual(value, prevValue)) {
                    let completesToRun = _.some(autocomplete, {field: key});

                    if (completesToRun) {
                        setAutocompleteState((as) => ({
                            lastRanOn: values,
                            loading: {
                                ...as.loading,
                                [key]: _.get(autocompleteState.loading, key, 0) + 1,
                            },
                        }));
                        performAutocomplete(entityType.dataTypeId, key, values)
                            .then((res) => {
                                let message = {};
                                let status = {};
                                for (const cmd of res) {
                                    if (cmd.discriminatorValue == 'SetValueCommand') {
                                        setFieldValue(cmd.field, cmd.value);
                                    }
                                    if (cmd.discriminatorValue == 'ValidationErrorCommand') {
                                        setFieldError(cmd.field, cmd.message);
                                    }
                                    if (cmd.discriminatorValue == 'SetMessageCommand') {
                                        message[cmd.field ?? key] = cmd.message;
                                        if (!status[cmd.field ?? key]) status[cmd.field ?? key] = 'success';
                                    }
                                    if (cmd.discriminatorValue == 'SetStatusCommand') {
                                        status[cmd.field ?? key] = cmd.status;
                                    }
                                }
                                setAutocompleteState((as) => ({
                                    ...as,
                                    loading: {
                                        ...as.loading,
                                        [key]: _.get(as, key, 1) - 1,
                                    },
                                    statuses: {
                                        ...as.statuses,
                                        ...status,
                                    },
                                    messages: {
                                        ...as.messages,
                                        ...message,
                                    },
                                }));
                            })
                            .catch(() => {
                                setAutocompleteState((as) => ({
                                    ...as,
                                    loading: {
                                        ...as.loading,
                                        [key]: _.get(as, key, 1) - 1,
                                    },
                                    statuses: {
                                        ...as.statuses,
                                        [key]: 'error',
                                    },
                                    messages: {
                                        ...as.messages,
                                        [key]: 'Error',
                                    },
                                }));
                            });
                    }
                }
            });
        }, 500),
        [autocompleteState, autocomplete, setAutocompleteState, setFieldError, setFieldValue],
    );

    useEffect(() => checkForAutocompletes(values), [values]);

    return <AutocompleteContext.Provider value={autocompleteState}>{children}</AutocompleteContext.Provider>;
}

export default Autocomplete;
export {AutocompleteContext};
